import { fetchAuthSession } from "aws-amplify/auth";
import axios from "axios";
import * as rax from "retry-axios";

class Endpoint {
    constructor() {
        this.BackendEndBase = process.env.REACT_APP_BACKEND_API;
        this.tenants = this.BackendEndBase + "tenants";
        this.users = this.BackendEndBase + "users";
        this.permissions = this.BackendEndBase + "permissions";
        this.schemas = this.BackendEndBase + "schemas";
        this.datasets = this.BackendEndBase + "datasets";
        this.jobs = this.BackendEndBase + "jobs";
        this.events = this.BackendEndBase + "events";
        this.search = this.BackendEndBase + "search";
        this.dataParser = this.BackendEndBase + "dataparser";
        this.duplicates = this.BackendEndBase + "duplicates";
        this.aggregations = this.BackendEndBase + "aggregations";
        this.systemConnections = this.BackendEndBase + "systemconnections";
        this.changeDataCapture = this.BackendEndBase + "cdc";
        this.integration = this.BackendEndBase + "integrations";
        this.aiGateway = this.BackendEndBase + "ai-gateway";
        this.dataSyndication = this.BackendEndBase + "syndication";
        this.configuration = this.BackendEndBase + "configuration";
        this.comments = this.BackendEndBase + "comments";
        this.conversations = this.BackendEndBase + "conversations";
    }
}

export const Endpoints = new Endpoint();

class API {
    constructor() {
        this.axios = axios.create();
        this.axios.defaults.raxConfig = {
            retry: 5,
            noResponseRetries: 5,
            retryDelay: 500,
            backoffType: "exponential",
            jitter: "full",
            httpMethodsToRetry: ["GET", "HEAD", "DELETE", "PUT", "POST"],
            statusCodesToRetry: [[502, 503]],
        };
        this.interceptorId = rax.attach(this.axios);

        this.head = async (endpoint, header = {}, config = {}) => {
            const session = await fetchAuthSession();
            const token = session.tokens?.idToken?.toString();
            return this.axios.head(endpoint, {
                headers: {
                    Authorization: token,
                    "Content-Type": "application/json",
                    ...header,
                },
                ...config,
            });
        };

        this.get = async (endpoint, header = {}, config = {}) => {
            const session = await fetchAuthSession();
            const token = session.tokens?.idToken?.toString();
            try {
                // console.log("token", token);
                return this.axios.get(endpoint, {
                    headers: {
                        Authorization: token,
                        "Content-Type": "application/json",
                        ...header,
                    },
                    ...config,
                });
            } catch (error) {
                if (error.isAxiosError) {
                    if (error.response && error.response.status === 404) {
                        return {
                            data: null,
                        };
                    }
                }
                throw error;
            }
        };

        this.getAnonymous = async (endpoint, header = {}, config = {}) => {
            try {
                return this.axios.get(endpoint, {
                    headers: {
                        "Content-Type": "application/json",
                        ...header,
                    },
                    ...config,
                });
            } catch (error) {
                if (error.isAxiosError) {
                    if (error.response && error.response.status === 404) {
                        return {
                            data: null,
                        };
                    }
                }
                throw error;
            }
        };

        this.post = async (endpoint, data, header = {}, config = {}) => {
            const session = await fetchAuthSession();
            const token = session.tokens?.idToken?.toString();
            // console.log("token", token);
            return this.axios.post(endpoint, data, {
                headers: {
                    Authorization: token,
                    "Content-Type": "application/json",
                    ...header,
                },
                ...config,
            });
        };

        this.postAnonymous = async (endpoint, data, header = {}, config = {}) => {
            return this.axios.post(endpoint, data, {
                headers: {
                    "Content-Type": "application/json",
                    ...header,
                },
                ...config,
            });
        };

        this.put = async (endpoint, data, header = {}, config = {}) => {
            const session = await fetchAuthSession();
            const token = session.tokens?.idToken?.toString();
            return this.axios.put(endpoint, data, {
                headers: {
                    Authorization: token,
                    "Content-Type": "application/json",
                    ...header,
                },
                ...config,
            });
        };

        this.putAnonymous = async (endpoint, data, header = {}, config = {}) => {
            return this.axios.put(endpoint, data, {
                headers: {
                    "Content-Type": "application/json",
                    ...header,
                },
                ...config,
            });
        };

        this.delete = async (endpoint, data = null, header = {}, config = {}) => {
            const session = await fetchAuthSession();
            const token = session.tokens?.idToken?.toString();
            return this.axios.delete(endpoint, {
                headers: {
                    Authorization: token,
                    "Content-Type": "application/json",
                    ...header,
                },
                ...(data ? { data } : {}),
                ...config,
            });
        };
    }
}

export default API;
